<template>
  <v-container fluid v-if="slideshow">
    <v-row>
      <v-col cols="12">
        <v-sheet elevation="1" class="pa-4 mb-6">
          <div class="d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <v-btn text icon @click="$router.push({ name: 'MilestoneSlideshowsRoute' })">
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <h2 class="mb-0">Slideshow: {{ slideshow.title }}</h2>
            </div>
            <div class="d-flex align-center gap-4">
              <v-checkbox
                v-model="slideshow.active"
                label="Active"
                dense
                hide-details
                @change="saveActive"
                class="mr-4"
              ></v-checkbox>
              <v-btn
                color="primary"
                depressed
                @click="openAddSlide"
              >
                Add Slide
              </v-btn>
              <v-btn
                color="error"
                depressed
                :loading="removePending"
                :disabled="!slideshow"
                @click="confirmDelete = true"
              >
                Delete
              </v-btn>
            </div>
          </div>
        </v-sheet>
      </v-col>
    </v-row>

    <!-- Upload New Slide -->
    <v-dialog v-model="uploadDialog" max-width="400">
      <v-card>
        <v-card-title>Upload New Slide</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="newSlideDisplayTime"
            label="Display Time (seconds)"
            type="number"
            outlined
            dense
            clearable
            min="1"
          ></v-text-field>
          <v-text-field
            v-model.number="newSlideOrder"
            label="Order"
            type="number"
            outlined
            dense
            min="0"
          ></v-text-field>
          <v-file-input
            ref="fileupload"
            label="Upload Image"
            outlined
            dense
            show-size
            accept="image/*"
            prepend-icon="mdi-camera"
            @change="onFileChange"
            :loading="uploadLoading"
          ></v-file-input>
          <v-col cols="12" v-if="uploadedFile">
              <v-responsive>
                <v-img :src="uploadedFile.fileUrl" />
              </v-responsive>
            </v-col>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="uploadDialog = false">Cancel</v-btn>
          <v-btn color="primary" depressed @click="addSlide">Add Slide</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Existing Slides List -->
    <v-row v-if="slidesLoading">
      <v-col class="text-center">
        <heart-loader />
      </v-col>
    </v-row>

    <v-row v-if="currentSlides && currentSlides.length">
      <v-col cols="12">
        <v-sheet elevation="1" class="pa-4">
          <h3 class="mb-4">
            Slides ({{ currentSlides.length }})
            <span class="text--secondary text-caption ml-2">(drag to reorder)</span>
          </h3>

          <!-- SortableJS container - ref used for direct DOM manipulation -->
          <div
            ref="sortableContainer"
            class="slide-list d-flex flex-column"
          >
            <div
              v-for="(slideId, index) in slideOrder"
              :key="slideId"
              :data-id="slideId"
              class="d-flex align-center pa-4 mb-2 rounded slide-item elevation-1"
            >
              <!-- Drag handle + order number -->
              <div class="drag-handle d-flex align-center justify-center mr-3" style="width: 40px; cursor: grab;">
                <v-icon>mdi-drag</v-icon>
              </div>
              <v-chip class="mr-3" color="primary" text-color="white">{{ index + 1 }}</v-chip>

              <!-- Thumbnail -->
              <v-img
                v-if="getSlideById(slideId) && getSlideById(slideId).file_upload"
                :src="getSlideById(slideId).file_upload.fileUrl"
                max-width="120"
                max-height="80"
                contain
                class="rounded mr-3"
              />
              
              <v-icon v-if="!getSlideById(slideId) || !getSlideById(slideId).file_upload" class="mr-3 text--secondary">mdi-image-off</v-icon>

              <!-- Display time -->
              <div v-if="editingSlideId === slideId" class="d-flex align-center mr-3">
                <v-text-field
                  v-model.number="editingDisplayTime"
                  type="number"
                  dense
                  hide-details
                  single-line
                  width="60"
                  style="width: 60px; min-width: 60px;"
                  class="edit-time-input"
                  @blur="saveDisplayTime(slideId)"
                  @keyup.enter="saveDisplayTime(slideId)"
                  @keyup.escape="cancelEdit()"
                  autofocus
                />
                <span class="text-body-2 ml-1">sec</span>
              </div>
              <span v-else class="d-flex align-center mr-3 display-time-cell" @click="startEdit(slideId)">
                {{ getSlideById(slideId) ? getSlideById(slideId).display_time / 1000 : 0 }} sec
                <v-icon x-small class="ml-1">mdi-pencil</v-icon>
              </span>

              <v-spacer />

              <!-- Actions -->
              <v-btn x-small color="error" depressed @click="openDeleteSlideDialog(getSlideById(slideId))">
                Delete
              </v-btn>
            </div>
          </div>
        </v-sheet>
      </v-col>
    </v-row>

    <v-row v-if="!currentSlides || !currentSlides.length">
      <v-col class="text-center text--secondary">
        No slides yet. Upload an image above to add your first slide.
      </v-col>
    </v-row>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="confirmDelete" max-width="400">
      <v-card>
        <v-card-title>Delete Slideshow?</v-card-title>
        <v-card-text>
          This will permanently delete this slideshow and all of its slides. This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDelete = false">Cancel</v-btn>
          <v-btn color="error" depressed :loading="removePending" @click="deleteSlideshow">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirmDeleteSlide" max-width="400">
      <v-card>
        <v-card-title>Delete Slide?</v-card-title>
        <v-card-text>
          This will permanently delete this slide. This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDeleteSlide = false">Cancel</v-btn>
          <v-btn color="error" depressed :loading="removePending" @click="deleteSlide">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Sortable from 'sortablejs'
import { mapGetters } from 'vuex'
import axios from 'axios'
import heartLoader from '@/components/heartLoader.vue'

export default {
  name: 'MilestoneSlideshowDetailPage',

  components: {
    heartLoader,
  },

  data() {
    return {
      file: null,
      uploadedFile: null,
      uploadLoading: false,
      confirmDelete: false,
      confirmDeleteSlide: false,
      deleteSlideId: null,
      newSlideTitle: '',
      newSlideDisplayTime: 10,
      newSlideOrder: null,
      newSlideFile: null,
      uploadDialog: false,
      uploadSuccess: false,
      slideOrder: [], // Drag-ordered list of slide _ids
      sortableInstance: null,
      editingSlideId: null,
      editingDisplayTime: 0,
    }
  },

  watch: {
    currentSlides: {
      handler() {
        if (this.slideOrder.length === 0 && this.currentSlides.length > 0) {
          // Initialize drag order from database order on first load
          this.slideOrder = [...this.currentSlides]
            .sort((a, b) => {
              const aOrder = a.order !== undefined ? a.order : 999999
              const bOrder = b.order !== undefined ? b.order : 999999
              return aOrder - bOrder
            })
            .map(slide => slide.id)
          // Re-init sortable if it exists (slides were added/removed)
          this.initSortable()
        }
      },
      immediate: true,
    }
  },

  computed: {
    ...mapGetters({
      slides: 'milestone-slide/list'
    }),
    slideshow_id() {
      return this.$route.params.id
    },
    slideshow() {
      return this.$store.state['milestone-slideshow'].keyedById[this.slideshow_id]
    },
    slidesLoading() {
      return this.$store.state['milestone-slide'].isFindPending
    },
    createPending() {
      return this.$store.state['milestone-slide'].isCreatePending
    },
    removePending() {
      return this.$store.state['milestone-slideshow'].isRemovePending
    },
    patchPending() {
      return this.$store.state['milestone-slideshow'].isPatchPending
    },
    currentSlides() {
      return this.slides.filter(slide => slide.slideshow_id === this.slideshow.id)
    },
  },

  mounted() {
    this.initSortable()
  },

  beforeDestroy() {
    if (this.sortableInstance) {
      this.sortableInstance.destroy()
      this.sortableInstance = null
    }
  },

  methods: {
    getSlideById(id) {
      return this.slides.find(slide => slide.id === id)
    },
    startEdit(slideId) {
      const slide = this.getSlideById(slideId)
      if (!slide) return
      this.editingSlideId = slideId
      this.editingDisplayTime = slide.display_time / 1000 || 0
    },
    async saveDisplayTime(slideId) {
      if (!this.editingSlideId) return
      const newTime = Math.max(0, this.editingDisplayTime) // Ensure non-negative
      await this.$store.dispatch('milestone-slide/patch', [slideId, { display_time: newTime * 1000 }])
      this.editingSlideId = null
      this.editingDisplayTime = 0
    },
    cancelEdit() {
      this.editingSlideId = null
      this.editingDisplayTime = 0
    },
    syncDragOrderFromSlides() {
      // Re-sync slideOrder from server data after refresh
      if (this.currentSlides.length > 0) {
        this.slideOrder = [...this.currentSlides]
          .sort((a, b) => {
            const aOrder = a.order !== undefined ? a.order : 999999
            const bOrder = b.order !== undefined ? b.order : 999999
            return aOrder - bOrder
          })
          .map(slide => slide.id)
      }
    },
    initSortable() {
      if (!this.$refs.sortableContainer || !this.slideOrder.length) return

      // Destroy existing instance to avoid duplicates
      if (this.sortableInstance) {
        this.sortableInstance.destroy()
      }

      const container = this.$refs.sortableContainer
      const that = this
      this.sortableInstance = Sortable.create(container, {
        handle: '.drag-handle',
        animation: 150,
        ghostClass: 'sortable-ghost',
        chosenClass: 'sortable-chosen',
        onEnd() {
          // Reorder the slideOrder array to match DOM order
          //console.log(that)
          const container = that.$refs.sortableContainer
          if (!container) return
          const newOrder = Array.from(container.children).map(el => el.dataset.id)
          that.slideOrder = newOrder
          // Persist to backend
          that.onSlideDragEnd()
        },
      })
    },
    async onFileChange(file) {
      this.file = file
      await this.uploadImage()
    },
    async uploadImage() {
      if (this.file) {
        this.formData = new FormData()
        this.formData.append('file', this.file)
        this.formData.append('eccoType', 'milestone-slideshow')
        this.uploadLoading = true
        const response = await axios.post(`${process.env.VUE_APP_API_URL}/file-upload`, this.formData, {
          headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${this.$store.state.auth.accessToken}` }
        })

        this.uploadedFile = response.data
        this.uploadLoading = false
      }
    },
    async saveTitle() {
      if (!this.slideshow) return
      await this.$store.dispatch('milestone-slideshow/patch', [this.slideshow.id, {
        title: this.slideshow.title,
      }])
    },
    async saveActive() {
      if (!this.slideshow) return
      await this.$store.dispatch('milestone-slideshow/patch', [this.slideshow.id, {
        active: this.slideshow.active,
      }])
    },
    openAddSlide() {
      this.uploadDialog = true
      this.newSlideOrder = this.currentSlides.length > 0 ? Math.max(...this.currentSlides.map(s => s.order)) + 1 : 1
    },
    async addSlide() {
      if (!this.slideshow_id || !this.uploadedFile) return

      try {
        await this.$store.dispatch('milestone-slide/create', {
          layout: 'image',
          slideshow_id: this.slideshow_id,
          file_id: this.uploadedFile.id,
          title: this.newSlideTitle || null,
          order: this.newSlideOrder !== null && this.newSlideOrder !== undefined ? this.newSlideOrder : null,
          display_time: this.newSlideDisplayTime * 1000,
        })
        // Clear form
        this.newSlideTitle = ''
        this.newSlideOrder = null
        this.uploadedFile = null
        this.uploadSuccess = true
        this.uploadDialog = false
        await this.getSlides()
        this.syncDragOrderFromSlides()
      } catch (error) {
        console.error('Error creating slide:', error)
      }
    },
    openDeleteSlideDialog(slide) {
      if (!slide) return
      this.confirmDeleteSlide = true
      this.deleteSlideId = slide.id
    },
    async onSlideDragEnd() {
      if (!this.slideOrder.length) return

      try {
        await this.$store.dispatch('milestone-slide/reorderSlides', {
          slides: this.slideOrder.map(slideId => ({ id: slideId }))
        })
        // Force refresh from server after reorder completes, then re-sync drag order
        await this.getSlides()
        this.syncDragOrderFromSlides()
      } catch (error) {
        console.error('Error reordering slides:', error)
        // Re-sync from server on failure
        await this.getSlides()
      }
    },
    async deleteSlide() {
      const deletedId = this.deleteSlideId
      await this.$store.dispatch('milestone-slide/remove', deletedId)
      this.confirmDeleteSlide = false
      this.deleteSlideId = null

      // Refresh and renumber remaining slides
      await this.getSlides()
      this.syncDragOrderFromSlides()
      await this.renumberSlides()
    },
    async renumberSlides() {
      const patches = this.slideOrder.map((slideId, index) =>
        this.$store.dispatch('milestone-slide/patch', [slideId, { order: index + 1 }])
      )
      await Promise.all(patches)
    },
    async deleteSlideshow() {
      await this.$store.dispatch('milestone-slideshow/remove', this.slideshow_id)
      this.confirmDelete = false
      this.$router.push({ name: 'MilestoneSlideshowsRoute' })
    },
    async getSlides() {
      await this.$store.dispatch('milestone-slide/find', {
        query: {
          slideshow_id: this.slideshow_id,
          $limit: 1000,
          $sort: { order: 1 },
        }
      })
    }
  },

  async beforeMount() {
    // Ensure slideshow data is loaded
    if (!this.slideshow && this.slideshow_id) {
      await this.$store.dispatch('milestone-slideshow/get', this.slideshow_id)
    }
    // Load slides filtered by this slideshow
    await this.getSlides()
    this.initSortable()
  },
}
</script>

<style lang="scss">
.gap-4 {
  gap: 16px;
}

.slide-item {
  transition: box-shadow 0.2s ease;
}

.drag-handle {
  flex-shrink: 0;
  color: rgba(0, 0, 0, 0.38);

  &:active {
    cursor: grabbing;
  }
}

.sortable-ghost {
  opacity: 0.4;
  background: rgba(0, 0, 0, 0.04);
}

.sortable-chosen {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.display-time-cell {
  cursor: pointer;
  border-radius: 4px;
  padding: 2px 8px;
  transition: background-color 0.15s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  .v-icon {
    opacity: 0;
    transition: opacity 0.15s ease;
  }

  &:hover .v-icon {
    opacity: 0.7;
  }
}

.edit-time-input :deep(.v-input__control) {
  min-height: auto !important;
}

.edit-time-input :deep(input) {
  font-size: 14px;
  padding: 0;
  text-align: center;
}
</style>
